import React, { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import isArray from 'lodash/isArray';
import AptitudeTestContentLoader from '../../components/AptitudeTestContentLoader';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import ERROR_FIELD from '../../constants/error-fields';
import ROUTE from '../../constants/route';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import AptitudeTestForm from '../../scenes/Form/components/AptitudeTestForm';
import './styles.css';

const getTimestamp = time => new Date().getTime() + Number(time) * 1000;

const AptitudeTest = props => {
  const { history, location } = props;

  const api = useApi();

  const [state, setState] = useState({ QuestionID: null });

  const scrollToTop = () => {
    window.scrollTo(
      0,
      document.getElementsByClassName('app__content')[0].offsetTop
    );
  };

  useEffect(() => {
    const isConfirmed = location.state && location.state.confirmed;
    if (isConfirmed) {
      history.replace(location.pathname, null);
    }

    api.get(
      ENDPOINT.ENTITY.APTITUDE_TEST,
      { confirmed: isConfirmed },
      {
        handleError: () => {
          history.replace(ROUTE.CANDIDATE_APTITUDE);
        },
        handleSuccess: response => {
          setState({
            ...response.question,
            Time: getTimestamp(response.question.Time),
          });
        },
      }
    );
  }, []);

  const handleSubmit = (values, actions = {}) => {
    const { setErrors, setSubmitting, resetForm } = actions;
    api.post(
      ENDPOINT.ENTITY.APTITUDE_TEST_NEXT,
      {
        ...values,
        question: state.QuestionID,
        answered: true,
      },
      {
        handleError: response => {
          if (response.data && response.data.fields) {
            const obj = {};
            for (let [key, value] of Object.entries(response.data.fields)) {
              const error = isArray(value) ? value[0] : value;
              obj[key] = ERROR_FIELD[error] || `${error}`;
            }
            setErrors(obj);
            setSubmitting(false);
          } else {
            history.replace(ROUTE.CANDIDATE_APTITUDE);
          }
        },
        handleSuccess: response => {
          if (response.result) {
            history.replace(ROUTE.CANDIDATE_APTITUDE, { testCompleted: true });
          } else {
            setState({
              ...response.question,
              Time: getTimestamp(response.question.Time),
            });
            setSubmitting(false);
            resetForm();
            scrollToTop();
          }
        },
      }
    );
  };

  const handleTimeExpire = () => {
    setState({ QuestionID: null });
    BasicDialog({
      bodyRender: 'Time has run out',
      textSuccessBtn: 'Next Question',
      modeless: false,
      hasCloseButton: false,
    }).then(() => {
      api.post(
        ENDPOINT.ENTITY.APTITUDE_TEST_NEXT,
        {
          question: state.QuestionID,
          answered: false,
        },
        {
          handleError: () => history.replace(ROUTE.CANDIDATE_APTITUDE),
          handleSuccess: response => {
            if (response.result) {
              history.replace(ROUTE.CANDIDATE_APTITUDE, {
                testCompleted: true,
              });
            } else {
              setState({
                ...response.question,
                Time: getTimestamp(response.question.Time),
              });
              scrollToTop();
            }
          },
        }
      );
    });
  };

  const body = useMemo(
    () =>
      state.QuestionID ? (
        <AptitudeTestForm
          timestamp={state.Time}
          html={state.SourceString}
          text={state.QuestionName}
          type={state.AnswerType}
          options={state.AnswerOptions}
          onSubmit={handleSubmit}
          onTimeExpire={handleTimeExpire}
        />
      ) : (
        <>
          <AptitudeTestContentLoader />
        </>
      ),
    [state.QuestionID]
  );

  return (
    <PageLayout
      pageClass="aptitude-test-page"
      pageTitle="Aptitude Test"
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          {body}
        </CSSTransition>
      }
    />
  );
};

export default AptitudeTest;
